import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../components/ui/table';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Loader2, ShoppingCart, ArrowLeftRight, XCircle, CheckCircle2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
// Rates to 2 decimals (stored values can carry float noise, e.g. 14.499999999999998).
const pct = (v) => `${Number(v || 0).toFixed(2)}%`;
const zar = (n) => `R ${Number(n || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const STATUS_CLS = {
  active: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  pending_approval: 'bg-amber-100 text-amber-800 border-amber-200',
  pending_listing_approval: 'bg-amber-100 text-amber-800 border-amber-200',
  withdrawn_settled: 'bg-gray-100 text-gray-700 border-gray-200',
  awaiting_approval: 'bg-amber-100 text-amber-800 border-amber-200',
  declined_by_admin: 'bg-red-100 text-red-800 border-red-200',
  accepted: 'bg-blue-100 text-blue-800 border-blue-200',
  cancelled: 'bg-gray-100 text-gray-700 border-gray-200',
  pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  rejected: 'bg-red-100 text-red-800 border-red-200',
};

export default function MarketplacePage() {
  const { token } = useAuth();
  const [marketplace, setMarketplace] = useState([]);
  const [mine, setMine] = useState([]);
  const [portfolio, setPortfolio] = useState([]);
  const [carryTrades, setCarryTrades] = useState([]);
  const [carrySummary, setCarrySummary] = useState({ count: 0, total_accrued: 0, total_paid: 0 });
  const [loading, setLoading] = useState(true);
  const [offerFor, setOfferFor] = useState(null);
  const [listFor, setListFor] = useState(null);

  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  const load = async () => {
    try {
      setLoading(true);
      const [m, mn, p, ct] = await Promise.all([
        axios.get(`${API_URL}/api/investor/listings/marketplace`, authHeader),
        axios.get(`${API_URL}/api/investor/listings/mine`, authHeader),
        axios.get(`${API_URL}/api/investor/my-tranches`, authHeader).catch(() => ({ data: { items: [] } })),
        axios.get(`${API_URL}/api/investor/carry-trades`, authHeader).catch(() => ({ data: { items: [], summary: {} } })),
      ]);
      setMarketplace(m.data.items || []);
      setMine(mn.data.items || []);
      setPortfolio(p.data.items || []);
      setCarryTrades(ct.data.items || []);
      setCarrySummary(ct.data.summary || { count: 0, total_accrued: 0, total_paid: 0 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load marketplace');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const cancel = async (id) => {
    if (!window.confirm('Cancel this listing?')) return;
    try {
      await axios.post(`${API_URL}/api/investor/listings/${id}/cancel`, {}, authHeader);
      toast.success('Listing cancelled');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const respondOffer = async (listingId, offerId, action) => {
    try {
      const r = await axios.post(`${API_URL}/api/investor/listings/${listingId}/offers/${offerId}/${action}`, {}, authHeader);
      toast.success(r.data?.message || `Offer ${action}ed`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="space-y-6" data-testid="marketplace-page">
      <div>
        <h1 className="text-2xl font-bold text-[#1D2D2A] font-['Manrope']">Deal Marketplace</h1>
        <p className="text-[#6C757D] mt-1">List tranches for sale to other investors. Buyers counter-offer at rates between your listing rate and your original rate — the difference becomes your ongoing carry until the loan settles.</p>
      </div>

      <Tabs defaultValue="browse">
        <TabsList>
          <TabsTrigger value="browse" data-testid="tab-browse">Browse ({marketplace.length})</TabsTrigger>
          <TabsTrigger value="mine" data-testid="tab-mine">My Listings ({mine.length})</TabsTrigger>
          <TabsTrigger value="carry" data-testid="tab-carry">My Carry Trades ({carrySummary.count || carryTrades.length})</TabsTrigger>
          <TabsTrigger value="new" data-testid="tab-new">List a Position</TabsTrigger>
        </TabsList>

        <TabsContent value="browse" className="mt-4">
          <Card><CardContent className="pt-6">
            {loading ? <Loader2 className="h-6 w-6 animate-spin mx-auto" />
              : marketplace.length === 0 ? <div className="py-10 text-center text-[#6C757D]">No active listings right now.</div>
              : (
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Loan</TableHead><TableHead>Client</TableHead><TableHead className="text-right">Accrued price (today)</TableHead>
                    <TableHead className="text-right">Listing rate</TableHead><TableHead className="text-right">Seller rate</TableHead><TableHead />
                  </TableRow></TableHeader>
                  <TableBody>
                    {marketplace.map(l => (
                      <TableRow key={l.id} data-testid={`market-row-${l.id}`}>
                        <TableCell className="font-mono text-xs">{l.loan_id}</TableCell>
                        <TableCell>{l.client_name || '—'}</TableCell>
                        <TableCell className="text-right font-mono">{zar(l.accrued_principal_today || l.accrued_principal_at_listing)}</TableCell>
                        <TableCell className="text-right font-mono text-emerald-600">{pct(l.listing_rate)}</TableCell>
                        <TableCell className="text-right font-mono text-[#6C757D]">{pct(l.seller_rate)}</TableCell>
                        <TableCell>
                          <Button size="sm" onClick={() => setOfferFor(l)} data-testid={`market-offer-${l.id}`} className="bg-[#18A37C] hover:bg-[#1D2D2A] text-white">
                            <ShoppingCart className="h-3 w-3 mr-1" /> Offer
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="mine" className="mt-4 space-y-3">
          {mine.length === 0 && <Card><CardContent className="py-10 text-center text-[#6C757D]">You haven&apos;t listed any positions yet.</CardContent></Card>}
          {mine.map(l => (
            <Card key={l.id} data-testid={`mine-row-${l.id}`}>
              <CardHeader className="flex flex-row items-start justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2 text-base"><ArrowLeftRight className="h-4 w-4" /> {l.loan_id}</CardTitle>
                  <CardDescription>{l.client_name || '—'} · listed at {pct(l.listing_rate)} · your rate {pct(l.seller_rate)} · price {zar(l.accrued_principal_at_listing)}</CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={`${STATUS_CLS[l.status]} border`}>{l.status}</Badge>
                  {['active', 'pending_approval', 'pending_listing_approval'].includes(l.status) && <Button size="sm" variant="outline" onClick={() => cancel(l.id)}>Cancel</Button>}
                </div>
              </CardHeader>
              <CardContent>
                {(l.offers || []).length === 0 ? (
                  <div className="text-sm text-[#6C757D]">No offers yet.</div>
                ) : (
                  <Table>
                    <TableHeader><TableRow>
                      <TableHead>Buyer</TableHead><TableHead className="text-right">Offered rate</TableHead><TableHead className="text-right">Implied carry</TableHead>
                      <TableHead>Status</TableHead><TableHead>Filed</TableHead><TableHead />
                    </TableRow></TableHeader>
                    <TableBody>
                      {l.offers.map(o => (
                        <TableRow key={o.id} data-testid={`offer-${o.id}`}>
                          <TableCell>{o.buyer_name || o.buyer_rp_code}</TableCell>
                          <TableCell className="text-right font-mono text-emerald-600">{pct(o.offered_rate)}</TableCell>
                          <TableCell className="text-right font-mono">{(l.seller_rate - o.offered_rate).toFixed(2)}%</TableCell>
                          <TableCell><Badge className={`${STATUS_CLS[o.status]} border`}>{o.status}</Badge></TableCell>
                          <TableCell className="text-xs">{o.created_at?.slice(0, 10)}</TableCell>
                          <TableCell>
                            {o.status === 'pending' && l.status === 'active' && (
                              <div className="flex gap-2">
                                <Button size="sm" onClick={() => respondOffer(l.id, o.id, 'accept')} data-testid={`accept-${o.id}`} className="bg-emerald-600 hover:bg-emerald-700 text-white"><CheckCircle2 className="h-3 w-3 mr-1" /> Accept</Button>
                                <Button size="sm" variant="outline" onClick={() => respondOffer(l.id, o.id, 'reject')} data-testid={`reject-${o.id}`}><XCircle className="h-3 w-3 mr-1" /> Reject</Button>
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="carry" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">My Carry Trades</CardTitle>
              <CardDescription>
                Positions where you sold a tranche and retained the spread as carry.
                {' '}Accrued: <span className="font-mono text-emerald-700">{zar(carrySummary.total_accrued)}</span>
                {' '}· Paid: <span className="font-mono">{zar(carrySummary.total_paid)}</span>
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? <Loader2 className="h-6 w-6 animate-spin mx-auto" />
                : carryTrades.length === 0 ? <div className="py-10 text-center text-[#6C757D]">You don&apos;t have any carry positions yet.</div>
                : (
                  <Table>
                    <TableHeader><TableRow>
                      <TableHead>Original loan</TableHead>
                      <TableHead>Sold as</TableHead>
                      <TableHead className="text-right">Carry rate</TableHead>
                      <TableHead className="text-right">Principal</TableHead>
                      <TableHead className="text-right">Accrued</TableHead>
                      <TableHead className="text-right">Paid</TableHead>
                      <TableHead>Since</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {carryTrades.map(c => (
                        <TableRow key={c.id} data-testid={`carry-row-${c.id}`}>
                          <TableCell className="font-mono text-xs">{c.original_loan_id}</TableCell>
                          <TableCell className="font-mono text-xs text-[#6C757D]">{c.source_loan_id}</TableCell>
                          <TableCell className="text-right font-mono">{Number(c.carry_rate || 0).toFixed(2)}%</TableCell>
                          <TableCell className="text-right font-mono">{zar(c.carry_principal)}</TableCell>
                          <TableCell className="text-right font-mono text-emerald-700">{zar(c.carry_accrued)}</TableCell>
                          <TableCell className="text-right font-mono">{zar(c.carry_paid)}</TableCell>
                          <TableCell className="text-xs">{(c.start_date || c.created_at || '').slice(0, 10)}</TableCell>
                          <TableCell><Badge className={`${STATUS_CLS[c.status] || 'bg-slate-100 text-slate-700 border-slate-200'} border`}>{c.status}</Badge></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="new" className="mt-4">
          <Card>
            <CardHeader><CardTitle>Pick a position to list</CardTitle><CardDescription>Only active tranches you own are shown. You&apos;ll set a listing rate lower than your own rate — the spread becomes your carry.</CardDescription></CardHeader>
            <CardContent>
              {portfolio.length === 0 ? <div className="py-6 text-center text-[#6C757D]">No active positions available to list.</div>
              : (
                <Table>
                  <TableHeader><TableRow><TableHead>Loan</TableHead><TableHead>Client</TableHead><TableHead className="text-right">Outstanding</TableHead><TableHead className="text-right">Rate</TableHead><TableHead /></TableRow></TableHeader>
                  <TableBody>
                    {portfolio.map(loan => (
                      <TableRow key={loan.loan_id || loan.id} data-testid={`portfolio-row-${loan.loan_id || loan.id}`}>
                        <TableCell className="font-mono text-xs">{loan.loan_id}</TableCell>
                        <TableCell>{loan.borrower_name || loan.client_name || '—'}</TableCell>
                        <TableCell className="text-right font-mono">{zar(loan.outstanding_balance || loan.closing_balance)}</TableCell>
                        <TableCell className="text-right font-mono">
                          {loan.rate_missing || Number(loan.interest_rate || 0) <= 0
                            ? <span className="text-amber-600 text-xs" title="No rate on file — you'll be prompted to enter one when listing">not on file</span>
                            : <>{Number(loan.interest_rate || 0).toFixed(2)}%</>}
                        </TableCell>
                        <TableCell><Button size="sm" onClick={() => setListFor(loan)} data-testid={`list-${loan.loan_id}`}>List for sale</Button></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <OfferDialog listing={offerFor} onOpenChange={o => !o && setOfferFor(null)} onSubmitted={() => { setOfferFor(null); load(); }} authHeader={authHeader} />
      <ListDialog loan={listFor} onOpenChange={o => !o && setListFor(null)} onSubmitted={() => { setListFor(null); load(); }} authHeader={authHeader} />
    </div>
  );
}

function OfferDialog({ listing, onOpenChange, onSubmitted, authHeader }) {
  const [rate, setRate] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { if (listing) setRate(String(listing.listing_rate)); }, [listing]);

  const submit = async () => {
    if (!listing) return;
    const r = Number(rate);
    if (!(r >= listing.listing_rate && r <= listing.seller_rate)) {
      toast.error(`Rate must be between ${pct(listing.listing_rate)} and ${pct(listing.seller_rate)}`);
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/api/investor/listings/${listing.id}/offers`, { offered_rate: r, note: note || null }, authHeader);
      toast.success(res.data?.message || 'Offer submitted');
      onSubmitted?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open={!!listing} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Make an offer</DialogTitle>
          <DialogDescription>
            {listing && (<>Loan <span className="font-mono">{listing.loan_id}</span> · seller lists at <strong>{pct(listing.listing_rate)}</strong> · maximum you can offer is <strong>{pct(listing.seller_rate)}</strong> (the seller&apos;s original rate).</>)}
          </DialogDescription>
        </DialogHeader>
        {listing && (
          <div className="space-y-3 py-2">
            <div><Label>Your offered rate (%)</Label>
              <Input data-testid="offer-rate" type="number" step="0.01" min={listing.listing_rate} max={listing.seller_rate} value={rate} onChange={e => setRate(e.target.value)} />
              <div className="text-xs text-[#6C757D] mt-1">Higher = you keep more of the coupon. Seller retains {(listing.seller_rate - Number(rate || 0)).toFixed(2)}% as carry.</div>
            </div>
            <div><Label>Note (optional)</Label><Textarea rows={2} value={note} onChange={e => setNote(e.target.value)} data-testid="offer-note" /></div>
            <div className="text-xs text-[#6C757D]">Price you would pay today: <span className="font-mono">{zar(listing.accrued_principal_today || listing.accrued_principal_at_listing)}</span></div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={submitting} data-testid="offer-submit">{submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending…</> : 'Submit offer'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ListDialog({ loan, onOpenChange, onSubmitted, authHeader }) {
  const [rate, setRate] = useState('');
  const [sellerRateOverride, setSellerRateOverride] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { setRate(''); setSellerRateOverride(''); setNote(''); }, [loan]);

  const rateMissing = !!loan && (loan.rate_missing || Number(loan.interest_rate || 0) <= 0);
  const effectiveSellerRate = rateMissing
    ? Number(sellerRateOverride || 0)
    : Number(loan?.interest_rate || 0);

  const submit = async () => {
    if (!loan) return;
    const r = Number(rate);
    if (rateMissing && (!effectiveSellerRate || effectiveSellerRate <= 0)) {
      toast.error('Enter your reference (seller) rate first — this tranche has no rate on file.');
      return;
    }
    if (!r || r <= 0 || r >= effectiveSellerRate) {
      toast.error(`Listing rate must be greater than 0 and lower than your ${rateMissing ? 'reference' : 'current'} rate (${effectiveSellerRate.toFixed(2)}%)`);
      return;
    }
    setSubmitting(true);
    try {
      const payload = { loan_id: loan.loan_id, listing_rate: r, note: note || null };
      if (rateMissing) payload.seller_rate_override = effectiveSellerRate;
      const res = await axios.post(`${API_URL}/api/investor/listings`, payload, authHeader);
      toast.success(res.data?.message || 'Listing submitted');
      onSubmitted?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open={!!loan} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>List for sale</DialogTitle>
          <DialogDescription>
            {loan && (
              <>
                Loan <span className="font-mono">{loan.loan_id}</span> · your rate {Number(loan.interest_rate || 0).toFixed(2)}% · outstanding {zar(loan.outstanding_balance || loan.closing_balance)}.
              </>
            )}
          </DialogDescription>
        </DialogHeader>
        {loan && (
          <div className="space-y-3 py-2">
            {rateMissing && (
              <div className="space-y-1 rounded-md border border-amber-200 bg-amber-50 p-3">
                <div className="text-xs text-amber-800">This tranche has no rate on file. Enter your reference (seller) rate first — the listing rate must be strictly lower than it so you retain a positive carry.</div>
                <Label className="text-amber-900">Your seller / reference rate (%)</Label>
                <Input
                  data-testid="list-seller-rate-override"
                  type="number"
                  step="0.01"
                  min="0"
                  value={sellerRateOverride}
                  onChange={e => setSellerRateOverride(e.target.value)}
                  placeholder="e.g. 12.5"
                />
              </div>
            )}
            <div>
              <Label>Listing rate (%)</Label>
              <Input
                data-testid="list-rate"
                type="number"
                step="0.01"
                value={rate}
                onChange={e => setRate(e.target.value)}
                placeholder={effectiveSellerRate > 0 ? `< ${effectiveSellerRate.toFixed(2)}` : 'enter a positive rate'}
              />
              <div className="text-xs text-[#6C757D] mt-1">
                {effectiveSellerRate > 0
                  ? <>Must be strictly lower than {effectiveSellerRate.toFixed(2)}% so you retain a positive carry ({effectiveSellerRate.toFixed(2)}% − listing rate).</>
                  : <>Enter your seller rate above first.</>}
              </div>
            </div>
            <div><Label>Note (optional)</Label><Textarea rows={2} value={note} onChange={e => setNote(e.target.value)} data-testid="list-note" /></div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={submitting} data-testid="list-submit">{submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Listing…</> : 'Create listing'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
